import { z } from 'zod';
import {
  ReferralDocumentType,
  defaultReferralDocumentType,
  singletonReferralDocumentTypes,
} from '../enums';
import { dateValidation } from '../../validation';

// A referral ID names both the Firestore document and the storage folder its
// documents live in, so it may only hold what is safe in a path. Firestore
// generates IDs of this shape. Protected web referral forms request one from
// the API; generateFirebaseId remains compatible for clients that still mint
// one locally.
export const referralIdPattern = /^[A-Za-z0-9_-]{1,64}$/;

export const isReferralId = (value: unknown): value is string =>
  typeof value === 'string' && referralIdPattern.test(value);

// A referral ID as a partner may send it: the API mints one on request
// (POST /referrals/generate-id) and accepts it back on the referral itself.
export const ReferralIdSchema = z
  .string()
  .trim()
  .regex(referralIdPattern, 'Referral ID must be alphanumeric');

// A referral cannot carry more documents than the referral forms an
// organization has always been allowed to upload from the web forms.
export const MAX_REFERRAL_DOCUMENTS = 20;

// Per document, decoded — the same ceiling storage.rules gives the web form
// uploads, so the rule reads the same for both transports (see below).
//
// Only the `path` transport can actually reach it. A document sent inline is
// bounded by the request instead: Cloud Run accepts 32MiB per HTTP/1 request
// (whole request, not just the body), the API caps the body at 32MB, and
// base64 costs 4 characters per 3 bytes — so ~23MiB of files per request, ALL
// documents of the referral together. See express.json in the functions app.
export const MAX_REFERRAL_DOCUMENT_SIZE_BYTES = 50 * 1024 * 1024;

// What a nurse can open in EBP and what the Word-to-PDF conversion and the
// AI care-document generation can read.
export const referralDocumentFileExtensions = [
  '.pdf',
  '.doc',
  '.docx',
  '.jpg',
  '.jpeg',
  '.png',
];

// Base64 as JSON payloads carry it: optionally as a data URL
// ("data:application/pdf;base64,JVBER...") and usually wrapped at 76
// characters. Both are normalized while parsing, so what is validated and
// decoded is the payload alone.
const normalizeBase64 = (value: unknown) =>
  typeof value === 'string'
    ? value.replace(/^data:[^;,]*;base64,/i, '').replace(/\s+/g, '')
    : value;

// Size of the file the base64 payload encodes, without decoding it: every 4
// characters carry 3 bytes, minus the padding at the end.
export const decodedBase64SizeBytes = (base64: string) => {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
};

const hasReferralDocumentExtension = (fileName: string) =>
  referralDocumentFileExtensions.some((extension) =>
    fileName.toLowerCase().endsWith(extension),
  );

const documentTypeMessage = `Document type must be one of the following values: ${Object.values(
  ReferralDocumentType,
).join(', ')}`;

const documentExtensionMessage = `A document must be one of ${referralDocumentFileExtensions.join(
  ', ',
)}`;

// A referral holds one consent and one clinical report; a payload sending two
// of either is a mistake worth reporting rather than one silently kept. The
// same rule guards the body array and the stored one, which name the type
// field the same way but the file differently.
const requiresSingletonReferralDocumentTypes = (
  ctx: z.core.ParsePayload<Array<{ type?: ReferralDocumentType }>>,
) => {
  const documents = ctx.value ?? [];

  const duplicated = singletonReferralDocumentTypes.find(
    (type) =>
      documents.filter(
        (document) => (document.type ?? defaultReferralDocumentType) === type,
      ).length > 1,
  );

  if (!duplicated) {
    return;
  }

  ctx.issues.push({
    code: 'custom',
    input: ctx.value,
    message: `A referral can have only one "${duplicated}" document`,
  });
};

const isValidBase64 = (content: string) =>
  content.length % 4 === 0 && /^[A-Za-z0-9+/]+={0,2}$/.test(content);

// The inline content of a document, validated without decoding it.
export const referralDocumentBase64Schema = (requiredMessage?: string) => {
  const content = z
    .string(requiredMessage)
    .refine(
      (value) => !value || isValidBase64(value),
      'Document contentBase64 is not valid base64',
    )
    .refine(
      (value) =>
        !value ||
        decodedBase64SizeBytes(value) <= MAX_REFERRAL_DOCUMENT_SIZE_BYTES,
      `A document cannot be larger than ${
        MAX_REFERRAL_DOCUMENT_SIZE_BYTES / (1024 * 1024)
      }MB`,
    );

  return z.preprocess(
    normalizeBase64,
    requiredMessage ? content.min(1, requiredMessage) : content,
  );
};

const referralDocumentNameSchema = z
  .string('Document name is required')
  .trim()
  .min(1, 'Document name is required')
  .refine(hasReferralDocumentExtension, documentExtensionMessage);

// One document attached to a referral. `type` is optional in the body and
// normalized to `supporting` while parsing, so a stored document always
// carries a concrete type.
//
// Exactly one of the two transports is required:
//  - `contentBase64` — the file inline, for system-to-system partners that
//    have nowhere to upload it first (Healix);
//  - `path` — a file already uploaded to Firebase Storage under the
//    referral's own folder, as the web referral forms do.
export const ReferralDocumentSchema = z
  .object({
    name: referralDocumentNameSchema,
    type: z
      .enum(ReferralDocumentType, documentTypeMessage)
      .default(defaultReferralDocumentType),
    contentBase64: referralDocumentBase64Schema().optional(),
    path: z.string().trim().optional(),
  })
  .refine(
    (document) => !!document.contentBase64 !== !!document.path,
    'A document requires either contentBase64 or path, not both',
  );

// The canonical `documents` of a referral body, for every organization.
export const ReferralDocumentsSchema = z
  .array(ReferralDocumentSchema)
  .max(
    MAX_REFERRAL_DOCUMENTS,
    `A referral cannot have more than ${MAX_REFERRAL_DOCUMENTS} documents`,
  )
  .check(requiresSingletonReferralDocumentTypes)
  .optional();

export type ReferralDocument = z.infer<typeof ReferralDocumentSchema>;
export type ReferralDocuments = NonNullable<
  z.infer<typeof ReferralDocumentsSchema>
>;

// ---------------------------------------------------------------------------
// How the documents of a referral are stored
// ---------------------------------------------------------------------------

// One document of a referral as it is stored. Deliberately the SAME shape the
// body sends (ReferralDocumentSchema) minus the inline content: a stored
// document is a body document whose file has been uploaded, so `fileName` and
// `type` are named identically and `path` is now certain. `sentToPlatform` is
// the one field only storage has — when the document was pushed to the patient
// platform (the deprecated structure called it `uploadedToHealee`, after the
// platform Perci used to run on).
export const ReferralStoredDocumentSchema = z.object({
  type: z.enum(ReferralDocumentType, documentTypeMessage),
  name: z
    .string('Document name is required')
    .min(1, 'Document name is required')
    .refine(hasReferralDocumentExtension, documentExtensionMessage),
  path: z
    .string('Document path is required')
    .min(1, 'Document path is required'),
  sentToPlatform: z
    .object({
      at: dateValidation(),
      by: z.string().nullish(),
    })
    .optional(),
});

export type ReferralStoredDocument = z.infer<
  typeof ReferralStoredDocumentSchema
>;
export type ReferralStoredDocuments = ReferralStoredDocument[];

// The body documents as they are stored: the same fields, minus the inline
// content of the ones that carried it. Every document must already know its
// storage path — the endpoint uploads the inline ones (and assigns their path)
// before the body is parsed.
export const toStoredReferralDocuments = (
  documents: ReferralDocuments,
): ReferralStoredDocuments =>
  documents.map(({ name, type, path }) => {
    if (!path) {
      throw new Error(
        `Document "${name}" has not been uploaded to the storage`,
      );
    }

    return { name, type, path };
  });

// The object structure the documents of a referral were stored in before
// PPL-3549 — one key per kind, with everything else in a `referrals` bucket.
// Still accepted from a caller that has not moved to the array yet, and the
// form the fireway migration converts the stored data from.
export // `uploadedToHealee` is the deprecated structure's own key name, mapped onto
// `sentToPlatform`; `name` and `path` it already called what we call them
type LegacyReferralDocument = {
  name: string;
  path: string;
  uploadedToHealee?: { at: Date; by?: string | null };
};

export type LegacyReferralDocuments = {
  latestClinicalReport?: LegacyReferralDocument;
  consent?: LegacyReferralDocument;
  referrals?: LegacyReferralDocument[];
};

// The deprecated structure records the platform upload under the name of the
// platform we used to run on
const fromLegacyReferralDocument = (
  { name, path, uploadedToHealee }: LegacyReferralDocument,
  type: ReferralDocumentType,
): ReferralStoredDocument => ({
  name,
  path,
  type,
  ...(uploadedToHealee ? { sentToPlatform: uploadedToHealee } : {}),
});

export const fromLegacyReferralDocuments = (
  legacy: LegacyReferralDocuments,
): ReferralStoredDocuments => {
  const documents: ReferralStoredDocuments = [];

  if (legacy.latestClinicalReport) {
    documents.push(
      fromLegacyReferralDocument(
        legacy.latestClinicalReport,
        ReferralDocumentType.clinicalReport,
      ),
    );
  }
  if (legacy.consent) {
    documents.push(
      fromLegacyReferralDocument(
        legacy.consent,
        ReferralDocumentType.patientReferralConsent,
      ),
    );
  }
  (legacy.referrals ?? []).forEach((document) => {
    if (document) {
      documents.push(
        fromLegacyReferralDocument(document, ReferralDocumentType.supporting),
      );
    }
  });

  return documents;
};

// Now that the two name their fields identically, what tells them apart is
// completeness: a stored document has both a `path` and a `type` and carries no
// content, so it needs nothing from the body schema. Anything else does — the
// `path` transport may omit `type`, which the body schema defaults, and inline
// content has to be validated and uploaded.
const isBodyReferralDocument = (document: unknown) =>
  !!document &&
  typeof document === 'object' &&
  ('contentBase64' in document ||
    !('path' in document) ||
    !('type' in document));

// The documents of a referral accept three forms while parsing, and store
// one: the canonical body array (validated by its own rules first), the
// stored array as it is, and the deprecated object structure.
const toStoredReferralDocumentsForm = (value: unknown) => {
  if (!value) {
    return value;
  }

  if (!Array.isArray(value)) {
    return fromLegacyReferralDocuments(value as LegacyReferralDocuments);
  }

  if (!value.some(isBodyReferralDocument)) {
    return value;
  }

  // The documents of the body are validated by their own rules — as one
  // array, so the rules over all of them hold too — and mapped back into
  // their place, next to the stored ones a web form sends. A body document
  // that breaks its own rules throws here, and the endpoint reports it as the
  // validation error it is (see getErrors).
  const bodyDocuments = toStoredReferralDocuments(
    ReferralDocumentsSchema.parse(value.filter(isBodyReferralDocument)) ?? [],
  );

  let mapped = 0;
  return value.map((document) =>
    isBodyReferralDocument(document) ? bodyDocuments[mapped++] : document,
  );
};

// The documents of a referral, as stored — one typed array for every
// organization. What an organization REQUIRES of it is a validation rule on
// top (see the per-organization schemas), never a different shape.
export const ReferralStoredDocumentsSchema = z
  .preprocess(
    toStoredReferralDocumentsForm,
    z
      .array(ReferralStoredDocumentSchema)
      .max(
        MAX_REFERRAL_DOCUMENTS,
        `A referral cannot have more than ${MAX_REFERRAL_DOCUMENTS} documents`,
      )
      .check(requiresSingletonReferralDocumentTypes),
  )
  .optional();

export type ReferralStoredDocumentsData = z.infer<
  typeof ReferralStoredDocumentsSchema
>;

export const findReferralDocumentOfType = (
  documents: ReferralStoredDocuments | undefined,
  type: ReferralDocumentType,
) => documents?.find((document) => document.type === type);

export const filterReferralDocumentsOfType = (
  documents: ReferralStoredDocuments | undefined,
  type: ReferralDocumentType,
) => documents?.filter((document) => document.type === type) ?? [];

// An organization's rule that a referral cannot be created without a
// document of a given type — the messages the referrers know, kept on the
// organization's own schema.
export const requiresReferralDocumentOfType = (
  type: ReferralDocumentType,
  message: string,
) =>
  [
    (documents?: ReferralStoredDocuments) =>
      !!findReferralDocumentOfType(documents, type),
    message,
  ] as const;

// The body of the one-document upload endpoint (POST
// /referrals/{referralId}/documents): the same document as the referral body
// carries, except that the file must be inline — the endpoint mints the
// storage path itself, so a caller never names it.
export const ReferralDocumentUploadSchema = z.object({
  name: referralDocumentNameSchema,
  type: z
    .enum(ReferralDocumentType, documentTypeMessage)
    .default(defaultReferralDocumentType),
  contentBase64: referralDocumentBase64Schema(
    'Document contentBase64 is required',
  ),
});

export type ReferralDocumentUpload = z.infer<
  typeof ReferralDocumentUploadSchema
>;
